import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, Package, Calendar, Trophy, TrendingUp, X, Star, Bell, Table2, Gamepad2, Settings, Layers, Database, Shield, BookOpen, DollarSign } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';

import {
  ControlDashboard, ControlProducts, ControlBoosters, ControlEvents,
  ControlTables, ControlGameReservations, ControlProductReservations,
  ControlLoyalty, ControlUsers, ControlSettings, ControlCardSync, ControlRoles
} from '../components/admin';
import ControlTableReservations from '../components/admin/ControlTableReservations';
import ControlOrders from '../components/admin/ControlOrders';
import ControlStockNotifications from '../components/admin/ControlStockNotifications';
import ControlCollections from '../components/admin/ControlCollections';
import ControlBilling from '../components/admin/ControlBilling';

/** Stable empty permissions so `useCallback(..., [staffPermissions])` does not change every render for admins. */
const EMPTY_STAFF_PERMISSIONS = Object.freeze([]);

export default function ControlPanel() {
  const { user, setUser } = useAuth();
  const { lang, t } = useLang();
  const getDefaultTab = () => {
    if (!user || user.role === 'admin') return 'dashboard';
    if (user.role === 'staff' && user.role_permissions?.length > 0) {
      return user.role_permissions[0];
    }
    return 'dashboard';
  };
  const [tab, setTab] = useState(() => getDefaultTab());
  const [message, setMessage] = useState('');
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [highlightOrderId, setHighlightOrderId] = useState('');

  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [productReservations, setProductReservations] = useState([]);
  const [tables, setTables] = useState([]);
  const [gameReservations, setGameReservations] = useState([]);
  const [tableReservations, setTableReservations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stockNotifications, setStockNotifications] = useState([]);
  const [collections, setCollections] = useState([]);
  const [storeSettings, setStoreSettings] = useState(null);
  const [boosters, setBoosters] = useState([]);
  const [roles, setRoles] = useState([]);
  const [billingIssuesCount, setBillingIssuesCount] = useState(0);
  const fetchAllGenerationRef = useRef(0);

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff';
  const hasAccess = isAdmin || isStaff;
  const staffPermissions = isStaff
    ? (Array.isArray(user?.role_permissions) && user.role_permissions.length > 0
        ? user.role_permissions
        : EMPTY_STAFF_PERMISSIONS)
    : EMPTY_STAFF_PERMISSIONS;
  const hasPermission = useCallback((key) => {
    if (isAdmin) return true;
    if (!isStaff) return false;
    return staffPermissions.includes(key);
  }, [isAdmin, isStaff, staffPermissions]);

  const canSeeTab = (key) => {
    return hasPermission(key);
  };

  const fetchAllEventsForAdmin = useCallback(async () => {
    const [eventsRes, tournamentsRes] = await Promise.all([
      axios.get(`${API}/events`, { params: { status: '', event_type: 'event' } }).catch(() => ({ data: [] })),
      axios.get(`${API}/events`, { params: { status: '', event_type: 'tournament' } }).catch(() => ({ data: [] })),
    ]);
    const merged = [...(Array.isArray(eventsRes.data) ? eventsRes.data : []), ...(Array.isArray(tournamentsRes.data) ? tournamentsRes.data : [])];
    // Mantener una entrada por event_id por si el backend ya devuelve datos mezclados.
    const byId = new Map();
    merged.forEach((ev) => {
      if (ev?.event_id) byId.set(ev.event_id, ev);
    });
    return Array.from(byId.values());
  }, []);

  const fetchAll = useCallback(async () => {
    if (!hasAccess) return;
    const gen = ++fetchAllGenerationRef.current;
    try {
      const requestIfAllowed = (allowed, requestPromise, fallback) => (allowed ? requestPromise : Promise.resolve(fallback));
      const [s, p, e, u, pr, tb, gr, st, bs, rl, trr, ord, sn, cn] = await Promise.all([
        requestIfAllowed(hasPermission('dashboard'), axios.get(`${API}/admin/dashboard`).catch(() => ({ data: null })), { data: null }),
        requestIfAllowed(hasPermission('products'), axios.get(`${API}/products?limit=50`).catch(() => ({ data: { products: [] } })), { data: { products: [] } }),
        requestIfAllowed(hasPermission('events'), fetchAllEventsForAdmin().catch(() => []), []),
        requestIfAllowed(hasPermission('users'), axios.get(`${API}/admin/users`).catch(() => ({ data: { users: [] } })), { data: { users: [] } }),
        requestIfAllowed(isAdmin, axios.get(`${API}/product-reservations/admin/all`).catch(() => ({ data: { reservations: [] } })), { data: { reservations: [] } }),
        requestIfAllowed(hasPermission('tables'), axios.get(`${API}/admin/tables`).catch(() => ({ data: { tables: [] } })), { data: { tables: [] } }),
        requestIfAllowed(hasPermission('gameRes'), axios.get(`${API}/admin/game-reservations`).catch(() => ({ data: { reservations: [] } })), { data: { reservations: [] } }),
        requestIfAllowed(isAdmin, axios.get(`${API}/admin/settings`).catch(() => ({ data: null })), { data: null }),
        requestIfAllowed(hasPermission('boosters'), axios.get(`${API}/admin/boosters`).catch(() => ({ data: { boosters: [] } })), { data: { boosters: [] } }),
        requestIfAllowed(isAdmin, axios.get(`${API}/admin/roles`).catch(() => ({ data: { roles: [] } })), { data: { roles: [] } }),
        requestIfAllowed(hasPermission('tableRes'), axios.get(`${API}/admin/table-reservations`).catch(() => ({ data: { reservations: [] } })), { data: { reservations: [] } }),
        requestIfAllowed(isAdmin, axios.get(`${API}/admin/orders`).catch(() => ({ data: { orders: [] } })), { data: { orders: [] } }),
        requestIfAllowed(hasPermission('stockAlerts'), axios.get(`${API}/admin/stock-notifications`).catch(() => ({ data: { notifications: [] } })), { data: { notifications: [] } }),
        requestIfAllowed(
          isAdmin,
          axios.get(`${API}/admin/collections`, { params: { is_public: false } }).catch(() => ({ data: { collections: [] } })),
          { data: { collections: [] } }
        ),
      ]);
      if (gen !== fetchAllGenerationRef.current) return;
      if (s.data) setStats(s.data);
      setProducts(p.data.products || []);
      setEvents(Array.isArray(e) ? e : []);
      setUsers(u.data.users || []);
      setProductReservations(pr.data.reservations || []);
      setTables(tb.data.tables || []);
      setGameReservations(gr.data.reservations || []);
      setBoosters(bs.data.boosters || []);
      if (st.data) setStoreSettings(st.data);
      setRoles(rl.data.roles || []);
      setTableReservations(trr.data.reservations || []);
      setOrders(ord.data.orders || []);
      setStockNotifications(sn.data.notifications || []);
      setCollections(cn.data.collections || []);

      if (isAdmin) {
        const billingRec = await axios.get(`${API}/admin/billing/reconciliation`, {
          params: { period: 'all', verify_with_stripe: true },
        }).catch(() => ({ data: null }));
        if (gen !== fetchAllGenerationRef.current) return;
        setBillingIssuesCount(Number(billingRec?.data?.total_issues ?? billingRec?.data?.issues_count ?? 0));
      } else {
        if (gen !== fetchAllGenerationRef.current) return;
        setBillingIssuesCount(0);
      }
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
    }
  }, [hasAccess, fetchAllEventsForAdmin, hasPermission, isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!isStaff || user?.role_permissions || !user?.staff_role_id) return;
    setPermissionsLoading(true);
    axios.get(`${API}/auth/my-permissions`)
      .then(({ data }) => {
        if (data.role_permissions?.length > 0) {
          setUser(prev => ({ ...prev, role_permissions: data.role_permissions, role_name: data.role_name }));
        }
      })
      .catch(() => {})
      .finally(() => setPermissionsLoading(false));
  }, [isStaff, setUser, user?.staff_role_id, user?.role_permissions]);

  useEffect(() => {
    if (isStaff && staffPermissions.length > 0 && !staffPermissions.includes(tab)) {
      setTab(staffPermissions[0]);
    }
  }, [isStaff, staffPermissions, tab]);

  const refreshProducts = async () => { const { data } = await axios.get(`${API}/products?limit=50`); setProducts(data.products || []); };
  const refreshEvents   = async () => { const data = await fetchAllEventsForAdmin(); setEvents(data || []); };
  const refreshBoosters = async () => { const { data } = await axios.get(`${API}/admin/boosters`); setBoosters(data.boosters || []); };
  const refreshRoles    = async () => { const { data } = await axios.get(`${API}/admin/roles`); setRoles(data.roles || []); };
  const goToOrder = useCallback((orderId) => {
    if (!orderId) return;
    setHighlightOrderId(orderId);
    setTab('orders');
  }, []);
  const consumeOrderHighlight = useCallback(() => {
    setHighlightOrderId('');
  }, []);

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm(t('admin.controlPanel.confirmDeleteEvent'))) return;
    try {
      await axios.delete(`${API}/events/${eventId}`);
      setEvents((prev) => prev.filter((ev) => ev.event_id !== eventId));
      setMessage(t('admin.controlPanel.eventDeleted'));
      await refreshEvents();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Error');
    }
  };

  const TAB_GROUPS = [
    { group: null, tabs: [{ key: 'dashboard', label: t('admin.controlPanel.tabs.dashboard'), icon: TrendingUp, adminOnly: false }] },
    {
      group: t('admin.controlPanel.groups.shop'),
      tabs: [
        { key: 'products', label: t('admin.controlPanel.tabs.products'), icon: Package, adminOnly: false },
        { key: 'orders', label: t('admin.controlPanel.tabs.orders'), icon: Package, adminOnly: true },
        { key: 'reservations', label: t('admin.controlPanel.tabs.reservations'), icon: Bell, adminOnly: true },
        { key: 'stockAlerts', label: t('admin.controlPanel.tabs.stockAlerts'), icon: Bell, adminOnly: false },
        { key: 'tables', label: t('admin.controlPanel.tabs.tables'), icon: Table2, adminOnly: false },
      ]
    },
    {
      group: t('admin.controlPanel.groups.reservations'),
      tabs: [
        { key: 'gameRes', label: t('admin.controlPanel.tabs.gameRes'), icon: Gamepad2, adminOnly: false },
        { key: 'tableRes', label: t('admin.controlPanel.tabs.tableRes'), icon: Table2, adminOnly: false },
      ]
    },
    {
      group: t('admin.controlPanel.groups.eventsTcg'),
      tabs: [
        { key: 'events', label: t('admin.controlPanel.tabs.events'), icon: Trophy, adminOnly: false },
        { key: 'boosters', label: t('admin.controlPanel.tabs.boosters'), icon: Layers, adminOnly: false },
        { key: 'cardSync', label: t('admin.controlPanel.tabs.cardSync'), icon: Database, adminOnly: false },
      ]
    },
    {
      group: t('admin.controlPanel.groups.community'),
      tabs: [
        { key: 'loyalty', label: t('admin.controlPanel.tabs.loyalty'), icon: Star, adminOnly: false },
        { key: 'users', label: t('admin.controlPanel.tabs.users'), icon: Users, adminOnly: false },
        { key: 'collections', label: t('admin.controlPanel.tabs.collections'), icon: BookOpen, adminOnly: true },
      ]
    },
    {
      group: t('admin.controlPanel.groups.settings'),
      tabs: [
        { key: 'billing', label: t('admin.controlPanel.tabs.billing'), icon: DollarSign, adminOnly: true },
        { key: 'roles', label: t('admin.controlPanel.tabs.roles'), icon: Shield, adminOnly: true },
        { key: 'settings', label: t('admin.controlPanel.tabs.settings'), icon: Settings, adminOnly: true },
      ]
    },
  ];

  const ALL_TABS = TAB_GROUPS.flatMap(g => g.tabs);
  const visibleTabs = ALL_TABS.filter(t => (t.adminOnly ? isAdmin : canSeeTab(t.key)));

  if (!user || !hasAccess) return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-center">
      <p className="text-muted-foreground">{t('admin.controlPanel.accessRestricted')}</p>
    </div>
  );

  if (isStaff && staffPermissions.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12 text-center">
        {permissionsLoading ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            <p className="text-muted-foreground text-sm">{t('admin.controlPanel.loadingPermissions')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-foreground font-semibold">{t('admin.controlPanel.noPermissionsConfigured')}</p>
            <p className="text-muted-foreground text-sm">{t('admin.controlPanel.noPermissionsDescription')}</p>
            <button onClick={() => window.location.reload()} className="btn-primary px-6 py-2 text-sm mt-2">
              {t('admin.controlPanel.retry')}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      {message && (
        <div className="fixed top-20 right-6 bg-card border border-primary text-foreground px-4 py-2 rounded-xl shadow-lg z-50 animate-fade-in">
          {message}
          <button onClick={() => setMessage('')} className="ml-3 text-muted-foreground"><X size={14} /></button>
        </div>
      )}

      {isStaff && (
        <div className="mb-4 px-4 py-2.5 bg-violet-500/10 border border-violet-500/20 rounded-xl flex items-center gap-2">
          <Shield size={15} className="text-violet-400" />
          <span className="text-violet-300 text-sm">
            {t('admin.controlPanel.staffAccessAs')} <strong>{user?.role_name || 'Staff'}</strong>{' - '}
            {t('admin.controlPanel.staffViewLimited')}
          </span>
        </div>
      )}

      {/* Mobile quick navigation */}
      <div className="lg:hidden mb-4">
        <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
          {t('admin.controlPanel.sectionLabel')}
        </label>
        <select
          value={tab}
          onChange={(e) => setTab(e.target.value)}
          className="w-full bg-card border border-border rounded-xl px-3 py-2.5 text-sm text-foreground"
        >
          {visibleTabs.map((t) => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Desktop sidebar navigation */}
        <aside className="hidden lg:block">
          <div className="sticky top-20 bg-card border border-border rounded-2xl p-3">
            {TAB_GROUPS.map((group, gIdx) => {
              const groupTabs = group.tabs.filter(t => (t.adminOnly ? isAdmin : canSeeTab(t.key)));
              if (groupTabs.length === 0) return null;
              return (
                <div key={gIdx} className={`${gIdx > 0 ? 'mt-4 pt-4 border-t border-border' : ''}`}>
                  {group.group && (
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground mb-2 px-2">
                      {group.group}
                    </p>
                  )}
                  <div className="space-y-1">
                    {groupTabs.map((t) => (
                      <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm transition-all ${
                          tab === t.key
                            ? 'bg-primary/15 text-primary border border-primary/40'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/40 border border-transparent'
                        }`}
                        data-testid={`admin-tab-${t.key}`}
                      >
                        <span className="flex items-center gap-2">
                          <t.icon size={14} />
                          {t.label}
                        </span>
                        {tab === t.key && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Content area */}
        <section className="min-w-0">
          {tab === 'dashboard'    && (
            <ControlDashboard
              stats={stats}
              lang={lang}
              onNavigate={setTab}
              billingIssuesCount={billingIssuesCount}
            />
          )}
          {tab === 'products'     && <ControlProducts products={products} boosters={boosters} onRefresh={refreshProducts} lang={lang} setMessage={setMessage} />}
          {tab === 'boosters'     && <ControlBoosters boosters={boosters} onRefresh={refreshBoosters} lang={lang} setMessage={setMessage} />}
          {tab === 'cardSync'     && <ControlCardSync lang={lang} setMessage={setMessage} />}
          {tab === 'events'       && <ControlEvents events={events} onRefresh={refreshEvents} onDelete={handleDeleteEvent} lang={lang} setMessage={setMessage} />}
          {tab === 'tables'       && <ControlTables tables={tables} setTables={setTables} lang={lang} setMessage={setMessage} />}
          {tab === 'gameRes'      && <ControlGameReservations reservations={gameReservations} setReservations={setGameReservations} lang={lang} setMessage={setMessage} />}
          {tab === 'reservations' && <ControlProductReservations reservations={productReservations} setReservations={setProductReservations} lang={lang} setMessage={setMessage} />}
          {tab === 'tableRes'     && <ControlTableReservations reservations={tableReservations} setReservations={setTableReservations} lang={lang} setMessage={setMessage} />}
          {tab === 'orders'       && (
            <ControlOrders
              orders={orders}
              setOrders={setOrders}
              lang={lang}
              setMessage={setMessage}
              highlightOrderId={highlightOrderId}
              onHighlightConsumed={consumeOrderHighlight}
            />
          )}
          {tab === 'stockAlerts'  && <ControlStockNotifications notifications={stockNotifications} setNotifications={setStockNotifications} lang={lang} setMessage={setMessage} />}
          {tab === 'collections'  && <ControlCollections collections={collections} setCollections={setCollections} lang={lang} setMessage={setMessage} />}
          {tab === 'billing'      && <ControlBilling lang={lang} setMessage={setMessage} onNavigateToOrder={goToOrder} />}
          {tab === 'loyalty'      && <ControlLoyalty users={users} lang={lang} setMessage={setMessage} />}
          {tab === 'users'        && <ControlUsers users={users} setUsers={setUsers} roles={roles} lang={lang} setMessage={setMessage} />}
          {tab === 'roles'        && <ControlRoles roles={roles} onRefresh={refreshRoles} lang={lang} setMessage={setMessage} />}
          {tab === 'settings'     && <ControlSettings storeSettings={storeSettings} lang={lang} setMessage={setMessage} />}
        </section>
      </div>
    </div>
  );
}
